import {downloadDocument, hasValue, IAttachmentData} from '@kcast-fe/core';

const DB_VERSION = 1;
const DB_NAME = 'malenia-attachments';
const STORE_NAME = 'attachments';

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        dbPromise = null;
        reject(request.error);
      };
    });
  }
  return dbPromise;
};

const idbGet = async (key: string): Promise<Blob | null> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result instanceof Blob ? request.result : null);
    request.onerror = () => reject(request.error);
  });
};

const idbPut = async (key: string, blob: Blob): Promise<void> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Returns the cached file content for the attachment, or null when it is not cached.
 * Storage failures degrade to null so the caller falls back to the network.
 */
export const getFileFromStorage = async (attachment: IAttachmentData): Promise<Blob | null> => {
  if (!hasValue(attachment.id)) {
    return null;
  }
  try {
    return await idbGet(attachment.id);
  } catch (e) {
    console.warn('[File] Failed to read file from storage.', e);
    return null;
  }
};

/**
 * Persists the file content for the attachment. Best effort: storage failures are
 * logged and swallowed so the caller can keep using the in-memory blob.
 */
export const saveFileToStorage = async (attachment: IAttachmentData, blob: Blob): Promise<void> => {
  if (!hasValue(attachment.id)) {
    return;
  }
  try {
    await idbPut(attachment.id, blob);
  } catch (e) {
    console.warn('[File] Failed to save file to storage.', e);
  }
};

// Several virtualized list items can mount at once and request the same file —
// share a single in-flight download per attachment id.
const inFlight = new Map<string, Promise<Blob>>();

/**
 * Single entry point for attachment content: returns the cached blob when present,
 * otherwise downloads it once (deduplicating concurrent requests) and caches it.
 * Resolves to null when the attachment has no id.
 */
export const getOrFetchFile = async (attachment: IAttachmentData): Promise<Blob | null> => {
  const id = attachment.id;
  if (!hasValue(id)) {
    return null;
  }

  const cached = await getFileFromStorage(attachment);
  if (cached) {
    return cached;
  }

  const pending = inFlight.get(id);
  if (pending) {
    return pending;
  }

  const request = downloadDocument(id)
    .then(async (blob) => {
      await saveFileToStorage(attachment, blob);
      return blob;
    })
    .finally(() => inFlight.delete(id));
  inFlight.set(id, request);
  return request;
};
